import { graphql, type DocumentType } from 'app/generated/gql'

/**
 * Queries behind the super-user SMS Ops tab's event lists. All on the public schema,
 * scoped to the session's org or workspace like every list query.
 *
 * The cards, charts and tables come from `smsAgentOpsMock.ts` until the ops resolvers in
 * docs/plans/2026-09-25-sms-agent-ops-analytics.md ("Backend contract") exist.
 */

/**
 * Thread change Events; the store keeps the ones that turned `aiEnabled` off. `data` is the
 * change diff the cause is read from (it can hold the agent brief, so it never renders);
 * `actor`, `journeyMeta` and `workflowIds` are left out as unused.
 */
export const SMS_OPS_TAKEOVER_EVENTS_QUERY = graphql(`
  query SmsOpsTakeoverEvents($search: String, $pagination: PaginationInput, $sort: SortInput) {
    events(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        orgId
        workspaceId
        locationId
        type
        typeId
        action
        source
        data
        eventDate
        createdDate
        updatedDate
        version
      }
      total
      page
    }
  }
`)

/**
 * Every `Message` field except its content (`text`, `subject`, `images`), for the Failed
 * Texts list and for Missed AI Replies (patient texts and the AI replies after them). Both
 * need delivery metadata only, so patient message bodies never load into an org-wide page.
 */
export const SMS_OPS_MESSAGES_QUERY = graphql(`
  query SmsOpsMessages($search: String, $pagination: PaginationInput, $sort: SortInput) {
    messages(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        orgId
        workspaceId
        locationId
        threadId
        contactId
        channel
        category
        direction
        source
        from
        to
        status
        errorCode
        errorMessage
        provider
        providerMessageId
        callerId
        isAgent
        isInitialMessage
        aiWorkflowId
        aiWorkflowName
        workflowRunId
        sentByUserId
        sendDate
        read
        archived
        createdDate
        updatedDate
        version
      }
      total
      page
    }
  }
`)

/** Threads for Waiting on a Reply. */
export const SMS_OPS_THREADS_QUERY = graphql(`
  query SmsOpsThreads($search: String, $pagination: PaginationInput, $sort: SortInput) {
    threads(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        orgId
        workspaceId
        locationId
        name
        phone
        lastIncomingMessageDate
        lastOutgoingMessageDate
        aiEnabled
        activeWorkflowId
        agentActivityId
        agentBrief
        agentBriefSetDate
        agentLastMessageDate
        agentReplyDueDate
        nameSetAtDate
        nameSetByUserId
        createdDate
        updatedDate
        version
      }
      total
      page
    }
  }
`)

export const SMS_OPS_OPT_OUTS_QUERY = graphql(`
  query SmsOpsOptOuts($search: String, $pagination: PaginationInput, $sort: SortInput) {
    optOuts(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        orgId
        workspaceId
        locationId
        key
        type
        createdDate
        updatedDate
        version
      }
      total
      page
    }
  }
`)

/**
 * AI conversation turns in range, for the Tool Errors list. Only what the list shows: no
 * reply text, tool arguments or results, or phone numbers, which carry patient details from
 * PMS lookups and would otherwise load org-wide. AI Reply Details loads a thread's full turns
 * with the inbox's debug query when a row is opened.
 */
export const SMS_OPS_AI_TURNS_QUERY = graphql(`
  query SmsOpsAiTurns($search: String, $pagination: PaginationInput, $sort: SortInput) {
    aiConversationTurns(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        orgId
        workspaceId
        locationId
        threadId
        model
        latencyMs
        steps {
          toolResults {
            toolName
            isError
          }
        }
        createdDate
      }
      total
      page
    }
  }
`)

/**
 * AI reply attempts with their outcome (TRK-5908): failed attempts for Failed AI Replies, and
 * the later successful ones that show a failed text was answered after all. No prompt, steps,
 * or message bodies: the list needs who, where, when, and why only.
 */
export const SMS_OPS_REPLY_ATTEMPTS_QUERY = graphql(`
  query SmsOpsReplyAttempts($search: String, $pagination: PaginationInput, $sort: SortInput) {
    aiConversationTurns(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        orgId
        workspaceId
        locationId
        threadId
        contactId
        messageId
        model
        failed
        failureReason
        failureMessage
        createdDate
      }
      total
      page
    }
  }
`)

export type SmsOpsEventRow = DocumentType<typeof SMS_OPS_TAKEOVER_EVENTS_QUERY>[`events`][`hits`][number]
export type SmsOpsMessageRow = DocumentType<typeof SMS_OPS_MESSAGES_QUERY>[`messages`][`hits`][number]
export type SmsOpsOptOutRow = DocumentType<typeof SMS_OPS_OPT_OUTS_QUERY>[`optOuts`][`hits`][number]
export type SmsOpsTurnRow = DocumentType<typeof SMS_OPS_AI_TURNS_QUERY>[`aiConversationTurns`][`hits`][number]
export type SmsOpsThreadRow = DocumentType<typeof SMS_OPS_THREADS_QUERY>[`threads`][`hits`][number]
export type SmsOpsAttemptRow = DocumentType<typeof SMS_OPS_REPLY_ATTEMPTS_QUERY>[`aiConversationTurns`][`hits`][number]
